import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { useFlags } from 'launchdarkly-react-client-sdk'
import styled, { useTheme } from 'styled-components/native'

import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { Display } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/display/Display'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DisplaySize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import useClientRiskStatus from './useClientRiskStatus'
import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { RiskBadgeWithPopover } from '../../../../../common/components/riskBadge/RiskBadgeWithPopover'
import { CLIENT_HOME } from '../../../../../common/constants/routingConstants'
import { getClientFullName, isEngageDAProvider } from '../../../../../common/utils/utils'
import { getAuthConfig } from '../../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { setSelectedClientId } from '../../../clientDetails/data/ltClientSlice'
import { ClientItem } from '../ClientListTable'
import ClientListActions from '../columns/ClientListActions'
import ClientListActivityInfo from '../columns/ClientListActivityInfo'
import ClientListProgramName from '../columns/ClientListProgramName'
import ClientListSessionDate from '../columns/ClientListSessionDate'
import ClientListSessionInfo from '../columns/ClientListSessionInfo'
import { sortLastAppointmentRow } from '../utils'

const HeaderCellContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const ClientNameContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const useClientListColumns = (programIds: string[], providerRoles: string[]) => {
  const { isPreferredNameEnabled } = useFlags()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const config = useSelector(getAuthConfig)
  const clientRiskStatus = useClientRiskStatus()

  const selectClient = useCallback(
    (client: ClientItem) => {
      track({
        event: mixpanelEvents.CLIENT_LIST_SELECT_CLIENT_NAME,
      })
      dispatch(setSelectedClientId(client.id))
      navigate(CLIENT_HOME.route)
    },
    [dispatch, navigate],
  )

  return useMemo(() => {
    const columnHelper = createColumnHelper<ClientItem>()
    const columns: ColumnDef<ClientItem, any>[] = [
      columnHelper.accessor((row) => getClientFullName(row, isPreferredNameEnabled), {
        id: 'Client Name',
        header: 'Client Name',
        enableSorting: true,
        meta: { flex: 1.5 },
        cell: (ctx) => {
          const riskEntry = isEngageDAProvider(providerRoles)
            ? (clientRiskStatus.get(ctx.row.original.id) ?? null)
            : null
          return (
            <ClientNameContainer>
              <Link text={ctx.getValue()} onPress={() => selectClient(ctx.row.original)} underline />
              {!config?.hideRiskAlerts && riskEntry && (
                <RiskBadgeWithPopover riskEntry={riskEntry} client={ctx.row.original} logNoteSource='clientList' />
              )}
            </ClientNameContainer>
          )
        },
      }),
    ]

    if (programIds.length > 1) {
      columns.push(
        columnHelper.accessor('program_name', {
          header: config?.programColumnHeader ?? 'Program',
          enableSorting: true,
          cell: (ctx) => {
            return (
              <ClientListProgramName
                programName={ctx.row.original.program_name as ProgramNames}
                isEngageDAProvider={isEngageDAProvider(providerRoles)}
              />
            )
          },
        }),
      )
    }
    columns.push(
      columnHelper.accessor('nextAppointment', {
        header: 'Next Session',
        enableSorting: true,
        meta: {
          inlineComponent: true,
        },
        cell: (ctx) => {
          return (
            <ClientListSessionDate
              nextAppointment={ctx.row.original.nextAppointment}
              lastAppointment={ctx.row.original.lastAppointment}
              providerRoles={providerRoles}
            />
          )
        },
        sortingFn: (rowA, rowB) => sortLastAppointmentRow(rowA.original, rowB.original),
      }),
      columnHelper.display({
        id: 'session_type',
        header: 'Session Type',
        meta: {
          inlineComponent: true,
        },
        cell: (ctx) => (
          <ClientListSessionInfo
            appointment={ctx.row.original.nextAppointment}
            hideIcon={isEngageDAProvider(providerRoles)}
          />
        ),
      }),
    )
    if (!isEngageDAProvider(providerRoles)) {
      columns.push(
        columnHelper.accessor('activityStatus', {
          meta: {
            inlineComponent: true,
          },
          header: () => (
            <HeaderCellContainer>
              <Display size={DisplaySize.EYEBROW}>Activity Status</Display>
              <Tooltip
                hoverEnabled
                placement='top'
                content={
                  <FormattedMessage
                    defaultMessage='For intake session this includes all forms associated with Care Onboarding. For follow up sessions this includes any activities you sent.'
                    description='Content for activity status tooltip'
                  />
                }
              >
                <InfoIcon size={24} fillColor={theme.colors.iconDefault} />
              </Tooltip>
            </HeaderCellContainer>
          ),
          cell: (ctx) => <ClientListActivityInfo activityStatus={ctx.row.original.activityStatus} />,
        }),
      )
    } else {
      // Engage DA providers
      columns.push(
        columnHelper.display({
          id: 'modality',
          header: 'Modality',
          meta: {
            inlineComponent: true,
          },
          cell: (ctx) => <ClientListSessionInfo appointment={ctx.row.original.nextAppointment} showModality />,
        }),
      )
    }
    columns.push(
      columnHelper.display({
        id: 'actions',
        header: () => <></>,
        meta: {
          inlineComponent: true,
        },
        cell: (ctx) => <ClientListActions clientId={ctx.row.original.id} />,
      }),
    )

    return columns
  }, [
    clientRiskStatus,
    config?.programColumnHeader,
    config?.hideRiskAlerts,
    isPreferredNameEnabled,
    programIds.length,
    providerRoles,
    selectClient,
    theme.colors.iconDefault,
  ])
}

export default useClientListColumns
